import { useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import {
    signIn, forgotPassword, forgotPasswordSubmit, completeNewPassword
} from 'aws-auth-lib';
import * as Yup from 'yup';
import { useAuth } from '../auth/AuthProvider';
import { ensureAuthSuccess } from '../utils/access';
import { requestEmailOtp, confirmEmailOtp } from '../services/authOtp';

const emailSchema = Yup.string().trim().email('Introduza um email válido.')
    .max(255).required('O email é obrigatório.');
const newPasswordSchema = {
    newPassword: Yup.string().max(255).required('Introduza a nova palavra-passe.'),
    confirmPassword: Yup.string().required('Confirme a palavra-passe.')
        .oneOf([Yup.ref('newPassword')], 'As palavras-passe devem coincidir.')
};

export const useSignIn = () => {
    const { loadAccess } = useAuth();
    const navigate = useNavigate();
    return useFormik({
        initialValues: { email: '', password: '', submit: null },
        validationSchema: Yup.object({
            email: emailSchema,
            password: Yup.string().max(255).required('A palavra-passe é obrigatória.')
        }),
        onSubmit: async ({ email, password }, { setErrors, setSubmitting }) => {
            try {
                const result = ensureAuthSuccess(await signIn(email.trim(), password));
                if (result?.isSignedIn) {
                    await loadAccess();
                    navigate('/', { replace: true });
                } else if (result?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                    navigate('/changePassword', { state: { nextStep: result.nextStep } });
                } else {
                    setErrors({ submit: 'Este método de confirmação ainda não está disponível neste portal.' });
                }
            } catch (error) {
                setErrors({ submit: error?.name === 'PasswordResetRequiredException'
                    ? 'É necessário recuperar a palavra-passe. Utilize a opção de recuperação.'
                    : 'Não foi possível iniciar sessão. Verifique o email e a palavra-passe.' });
            } finally {
                setSubmitting(false);
            }
        }
    });
};

export const useForgetPassword = () => {
    const navigate = useNavigate();
    return useFormik({
        initialValues: { email: '', submit: null },
        validationSchema: Yup.object({ email: emailSchema }),
        onSubmit: async ({ email }, { setErrors, setSubmitting }) => {
            try {
                const result = ensureAuthSuccess(await forgotPassword(email.trim()));
                if (result?.nextStep?.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
                    navigate('/forgetPassword/changePassword', { state: { email: email.trim() } });
                } else if (result?.nextStep?.resetPasswordStep === 'DONE') {
                    navigate('/login', { replace: true, state: { passwordReset: true } });
                } else {
                    throw new Error('UNSUPPORTED_RESET_STEP');
                }
            } catch {
                setErrors({ submit: 'Não foi possível iniciar a recuperação da palavra-passe. Tente novamente.' });
            } finally {
                setSubmitting(false);
            }
        }
    });
};

export const useForgetPasswordSubmit = (email) => {
    const navigate = useNavigate();
    return useFormik({
        initialValues: { validationCode: '', newPassword: '', confirmPassword: '', submit: null },
        validationSchema: Yup.object({
            validationCode: Yup.string().trim().required('Introduza o código recebido.'),
            ...newPasswordSchema
        }),
        onSubmit: async ({ validationCode, newPassword }, { setErrors, setSubmitting }) => {
            try {
                if (!email) throw new Error('EMAIL_UNAVAILABLE');
                ensureAuthSuccess(await forgotPasswordSubmit(email, validationCode.trim(), newPassword));
                navigate('/login', { replace: true, state: { passwordReset: true } });
            } catch {
                setErrors({ submit: 'Não foi possível alterar a palavra-passe. Verifique o código e a nova palavra-passe.' });
            } finally {
                setSubmitting(false);
            }
        }
    });
};

export const useCompleteNewPassword = () => {
    const { loadAccess } = useAuth();
    const navigate = useNavigate();
    return useFormik({
        initialValues: { newPassword: '', confirmPassword: '', submit: null },
        validationSchema: Yup.object(newPasswordSchema),
        onSubmit: async ({ newPassword }, { setErrors, setSubmitting }) => {
            try {
                const result = ensureAuthSuccess(await completeNewPassword(newPassword));
                if (!result?.isSignedIn) throw new Error('SIGN_IN_INCOMPLETE');
                await loadAccess();
                navigate('/', { replace: true });
            } catch {
                setErrors({ submit: 'Não foi possível definir a palavra-passe. Verifique os requisitos da conta ou volte a iniciar sessão.' });
            } finally {
                setSubmitting(false);
            }
        }
    });
};

export const useEmailOtpSignIn = () => {
    const { loadAccess } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState('email');
    const [message, setMessage] = useState('');
    const formik = useFormik({
        initialValues: { email: '', code: '', submit: null },
        validationSchema: Yup.object({
            email: emailSchema,
            code: step === 'code'
                ? Yup.string().required('O código é obrigatório.')
                    .matches(/^\d{8}$/, 'Introduza os 8 dígitos do código.')
                : Yup.string()
        }),
        onSubmit: async ({ email, code }, { setErrors, setSubmitting }) => {
            setMessage('');
            try {
                if (step === 'email') {
                    await requestEmailOtp(email);
                    setStep('code');
                } else {
                    await confirmEmailOtp(code);
                    await loadAccess();
                    navigate('/', { replace: true });
                }
            } catch {
                setErrors({ submit: step === 'email'
                    ? 'Não foi possível enviar o código. Confirme o email e tente novamente.'
                    : 'O código é inválido ou expirou. Tente novamente.' });
            } finally {
                setSubmitting(false);
            }
        }
    });
    const resendCode = async () => {
        formik.setSubmitting(true);
        formik.setErrors({});
        setMessage('');
        try {
            await requestEmailOtp(formik.values.email);
            formik.setFieldValue('code', '', false);
            setMessage('Enviámos um novo código de acesso para o seu email.');
        } catch {
            formik.setErrors({ submit: 'Não foi possível reenviar o código. Tente novamente.' });
        } finally {
            formik.setSubmitting(false);
        }
    };
    const changeEmail = () => {
        setStep('email');
        setMessage('');
        formik.setFieldValue('code', '', false);
        formik.setErrors({});
        formik.setTouched({});
    };
    return { formik, step, message, resendCode, changeEmail };
};
